import { linkedInProfileUrlFrom, type QrDecoder } from './linkedin-qr';
import type { FetchErrorLike } from '../_shared/fetch-error-types';
import type { Scan, ScanPatchInput } from './scan';

/**
 * The photo-intake pipeline — client half of U5 of
 * plans/2026-08-25-001-lead-scanner-implementation-plan.md, and the port of
 * the prototype's `confirmPhotos()` semantics
 * (docs/lead-scanner-prototype.html ~line 852).
 *
 * Every I/O seam is injected (`PhotoIntakeDeps`) rather than reached for:
 * this module holds the SEQUENCING and the ROUTING decisions, which are the
 * only parts with real behavior to get wrong, and injection is what makes
 * them assertable without a network, a DB, or the zxing WASM binary. The
 * React wiring (the actual `POST`/`PATCH`/`fetch` calls, react-query
 * invalidation, toasts) lives in `components/PhotoIntake.tsx`.
 *
 * The order below is the plan's, and the order matters:
 *
 *   1. Generate the draft's idempotency UUID client-side (Key Technical
 *      Decision 7 — stamped at draft-creation time so a later Retry reuses
 *      it).
 *   2. `POST /scans` a `processing` row per file. ALL of them, before any
 *      resolution work starts — the photo count is known up front, so the
 *      rep sees N rows appear at once rather than trickling in.
 *   3. `onBatchCreated` → `routeAfterIntake` (1 photo → straight to its
 *      review screen, which renders skeletons while the row is `processing`;
 *      2+ → back to Home with a toast).
 *   4. Per photo, concurrently: QR-decode the ORIGINAL `File` bytes. A
 *      payload matching the LinkedIn profile shape → `PATCH` the row to
 *      `draft` with ONLY `linkedin` set, no OCR call at all (Decision 4).
 *   5. Anything else — no QR, a non-LinkedIn QR (a conference
 *      registration code), or a decode exception — falls through to
 *      `POST /extract-badge/:scanId` with the raw bytes.
 *
 * Every leg is individually try/caught, so **a failed photo never blocks the
 * others** — including a failed `POST /scans`, which drops that one file out
 * of the batch instead of rejecting the whole intake.
 *
 * Photo resolution (steps 4/5) runs at `PHOTO_RESOLVE_CONCURRENCY`, not a flat
 * `Promise.all` (round-2 live-QA bug 4): firing every photo's extraction
 * request at once ran into the browser's ~6-connections-per-origin cap on a
 * ~13-photo bulk upload — several requests sat queued burning their client
 * abort-timeout budget before the request even started, and the client's
 * fail-closed OCR-failure PATCH could then race a still-running server
 * extraction and get overwritten later (or vice versa). This was documented
 * here as the intended fallback if flat `Promise.all` ever became a problem
 * — it did. Same bounded-concurrency-runner shape as action-view's
 * `GONG_BATCH_CONCURRENCY` pool (`mini-apps/action-view/api.ts`).
 */

/** The `POST /scans` body for a photo-intake row. `status`/`sourceType` are
 *  fixed for this path, so they aren't parameters — a photo intake always
 *  creates a `processing` `photo` row. */
export interface PhotoScanCreateInput {
  idempotencyKey: string;
  sourceType: 'photo';
  status: 'processing';
  campaignId: string;
  scannedByEmail: string;
  photoFilename: string;
}

/** Which leg a photo actually took, per photo. `failed` means the row could
 *  not be created or could not be handed off at all — never "the badge was
 *  unreadable", which is a normal `error`-status row the backend writes.
 *  `ocr-pending` (round-2 live-QA bug 4) is neither: the extraction request's
 *  outcome is genuinely unknown (an aborted/timed-out client request whose
 *  server-side handler may still be running) — the row is deliberately left
 *  at `processing` rather than guessed at, so it is never counted as a
 *  failure by callers filtering on `'failed'` (e.g. `PhotoIntake.tsx`'s
 *  batch-failure toast). */
export type PhotoIntakeLeg = 'linkedin' | 'ocr' | 'ocr-pending' | 'failed';

export interface PhotoOutcome {
  photoFilename: string;
  /** `null` only when `POST /scans` itself failed — there is no row. */
  scanId: string | null;
  leg: PhotoIntakeLeg;
  error?: string;
}

export interface PhotoIntakeDeps {
  /** `crypto.randomUUID` in the app; a counter in tests. */
  newIdempotencyKey: () => string;
  createProcessingScan: (input: PhotoScanCreateInput) => Promise<Scan>;
  patchScan: (scanId: string, patch: ScanPatchInput) => Promise<unknown>;
  /** `POST /extract-badge/:scanId` with the file's raw bytes (Key Technical
   *  Decision 3). Resolves when the server has already applied its own
   *  PATCH semantics to the row — this pipeline does not re-patch on
   *  success. */
  extractBadgeFromPhoto: (scanId: string, file: File) => Promise<unknown>;
  decodeQr: QrDecoder;
  /** Fired once, after every row exists and before any resolution — this is
   *  where `routeAfterIntake` runs. */
  onBatchCreated: (scanIds: string[]) => void;
  /** Per-photo completion hook (react-query invalidation in the app) so a
   *  resolved row shows up without waiting for the next poll tick. */
  onPhotoSettled?: (outcome: PhotoOutcome) => void;
}

export interface PhotoIntakeParams {
  files: File[];
  campaignId: string;
  scannedByEmail: string;
}

export interface PhotoIntakeResult {
  /** The batch's row ids, in file order, for the rows that were created. */
  scanIds: string[];
  outcomes: PhotoOutcome[];
}

/**
 * Shown on a row whose photo could not be handed to the extraction endpoint
 * at all (request rejected, network down, endpoint 5xx). Distinct from
 * `scan.ts`'s `PARTIAL_DETECTION_MESSAGE`, which means the model looked and
 * found no badge — that outcome is a normal, non-blocking `draft` advisory,
 * not an `error`, whereas this one genuinely is a stuck/unreadable-photo
 * error and keeps the row at `error`.
 *
 * Writing this message (and flipping the row to `error`) is not cosmetic: a
 * row left at `processing` forever would keep Home's "poll while anything is
 * processing" loop running forever and give the rep no way to act on the
 * photo. Failing closed to `error` both stops the poll and puts the row back
 * in the rep's Draft-scans list where they can retype it or delete it.
 */
export const PHOTO_EXTRACTION_UNAVAILABLE_MESSAGE =
  "Couldn't read this photo — the extraction service didn't respond. Add this lead by hand, or delete the draft and re-scan.";

function errorMessage(err: unknown): string {
  return (err as Error)?.message ?? String(err);
}

/**
 * True when `err` is a definitive server response — a `FetchErrorLike` with
 * a real HTTP `status`, meaning the request round-tripped and the server
 * itself already reported failure (e.g. a 502 from a genuine Anthropic API
 * failure). False for an ambiguous transport-level rejection — a client
 * abort-timeout (`AbortError`) or a network `TypeError` — where the server's
 * own handler may still be running and could still write a real outcome.
 *
 * This distinction only matters because `mini-apps/_shared/hooks/useApi.ts`'s
 * `apiFetch` wraps ONLY a completed non-2xx `Response` into a
 * `MiniAppFetchError` (`FetchErrorLike`, `status` always a number); a
 * rejected `fetch()` call itself propagates unwrapped and therefore never has
 * `.status`. Round-2 live-QA bug 4: the client's abort-timeout used to fire
 * well before the server's vision call finished on a bulk upload, and this
 * ambiguous case was being treated identically to a definitive failure —
 * PATCHing the row to `error` even though the server was still working and
 * would later overwrite it with the real (successful) result, or vice versa.
 */
function isDefinitiveServerError(err: unknown): err is FetchErrorLike {
  return typeof err === 'object' && err !== null && typeof (err as { status?: unknown }).status === 'number';
}

interface CreatedPhoto {
  scan: Scan;
  file: File;
}

/** Step 4/5 for ONE photo. Never rejects — it resolves with the leg it took
 *  so `Promise.all` over the batch is safe and one bad photo is isolated. */
async function resolvePhoto(created: CreatedPhoto, deps: PhotoIntakeDeps): Promise<PhotoOutcome> {
  const { scan, file } = created;
  const photoFilename = scan.photoFilename ?? file.name;

  // Decode the ORIGINAL File bytes, never a thumbnail — down-scaling
  // destroys the QR module pattern (prototype DEV NOTE, ~line 845).
  let payload: string | null = null;
  try {
    payload = await deps.decodeQr(file);
  } catch {
    // A decode failure is not a photo failure: it is exactly the "fall
    // through to vision OCR" case, same as finding no QR at all.
    payload = null;
  }

  const linkedin = payload === null ? null : linkedInProfileUrlFrom(payload);

  if (linkedin !== null) {
    try {
      await deps.patchScan(scan.id, { status: 'draft', linkedin });
      const outcome: PhotoOutcome = { photoFilename, scanId: scan.id, leg: 'linkedin' };
      deps.onPhotoSettled?.(outcome);
      return outcome;
    } catch (err) {
      // Best-effort fail-closed, same as the OCR leg below: a rejected
      // draft-PATCH after a successful decode would otherwise strand the row
      // in `processing` forever (see PHOTO_EXTRACTION_UNAVAILABLE_MESSAGE).
      try {
        await deps.patchScan(scan.id, {
          status: 'error',
          sfdcError: PHOTO_EXTRACTION_UNAVAILABLE_MESSAGE,
        });
      } catch {
        // Swallowed deliberately: the original PATCH error below is the one
        // worth reporting, and a failed error-marking PATCH must not
        // escalate into an unhandled rejection that takes the batch down.
      }
      const outcome: PhotoOutcome = {
        photoFilename,
        scanId: scan.id,
        leg: 'failed',
        error: errorMessage(err),
      };
      deps.onPhotoSettled?.(outcome);
      return outcome;
    }
  }

  try {
    await deps.extractBadgeFromPhoto(scan.id, file);
    const outcome: PhotoOutcome = { photoFilename, scanId: scan.id, leg: 'ocr' };
    deps.onPhotoSettled?.(outcome);
    return outcome;
  } catch (err) {
    if (!isDefinitiveServerError(err)) {
      // Ambiguous transport-level failure (round-2 live-QA bug 4) — the
      // server's own extraction handler may still be running and may still
      // write a real outcome (success -> draft, real failure -> error). Do
      // NOT patch the row: fail-closing here is exactly what produced false
      // "Unreadable badge" errors that later got silently overwritten by a
      // late-arriving real result. Leave the row at `processing` — the list
      // poll (`scansListPollInterval`) keeps checking, and `GET /scans`'s
      // stale-`processing` sweep (`api.ts`) is the only thing that ever
      // gives up on a genuinely abandoned one.
      const outcome: PhotoOutcome = {
        photoFilename,
        scanId: scan.id,
        leg: 'ocr-pending',
        error: errorMessage(err),
      };
      deps.onPhotoSettled?.(outcome);
      return outcome;
    }

    // Definitive server response (the request completed and the server
    // itself reported failure) — fail closed exactly as before, so the row
    // leaves `processing` (see PHOTO_EXTRACTION_UNAVAILABLE_MESSAGE). If even
    // this PATCH fails there is nothing further to try client-side; the row
    // is picked up as stale by the rep, not silently retried.
    try {
      await deps.patchScan(scan.id, {
        status: 'error',
        sfdcError: PHOTO_EXTRACTION_UNAVAILABLE_MESSAGE,
      });
    } catch {
      // Swallowed deliberately: the original extraction error below is the
      // one worth reporting, and a failed error-marking PATCH must not
      // escalate into an unhandled rejection that takes the batch down.
    }
    const outcome: PhotoOutcome = {
      photoFilename,
      scanId: scan.id,
      leg: 'failed',
      error: errorMessage(err),
    };
    deps.onPhotoSettled?.(outcome);
    return outcome;
  }
}

/** How many photos resolve (QR-decode + OCR-or-linkedin-PATCH) at once. See
 *  this file's header comment for why this is bounded rather than a flat
 *  `Promise.all` — round-2 live-QA bug 4. Same value as action-view's
 *  `GONG_BATCH_CONCURRENCY`. */
const PHOTO_RESOLVE_CONCURRENCY = 3;

export async function runPhotoIntake(
  params: PhotoIntakeParams,
  deps: PhotoIntakeDeps,
): Promise<PhotoIntakeResult> {
  const { files, campaignId, scannedByEmail } = params;
  if (files.length === 0) return { scanIds: [], outcomes: [] };

  // Step 1+2: every row first. `allSettled` (not `all`) so one rejected
  // `POST /scans` costs that one photo, not the batch.
  const creations = await Promise.allSettled(
    files.map((file) =>
      deps
        .createProcessingScan({
          idempotencyKey: deps.newIdempotencyKey(),
          sourceType: 'photo',
          status: 'processing',
          campaignId,
          scannedByEmail,
          photoFilename: file.name,
        })
        .then((scan): CreatedPhoto => ({ scan, file })),
    ),
  );

  const created: CreatedPhoto[] = [];
  const failedOutcomes: PhotoOutcome[] = [];
  creations.forEach((settled, index) => {
    if (settled.status === 'fulfilled') {
      created.push(settled.value);
      return;
    }
    failedOutcomes.push({
      photoFilename: files[index].name,
      scanId: null,
      leg: 'failed',
      error: errorMessage(settled.reason),
    });
  });

  // Step 3: route on the known-up-front count, before any resolution.
  const scanIds = created.map((c) => c.scan.id);
  deps.onBatchCreated(scanIds);

  // Steps 4/5, bounded concurrency (round-2 live-QA bug 4 — see header
  // comment). Order-preserving: each worker claims the next index and writes
  // its result into that same slot, so `resolvedOutcomes` ends up in file
  // order regardless of which worker finishes first. `resolvePhoto` never
  // rejects, so this pool never needs its own try/catch.
  const resolvedOutcomes: PhotoOutcome[] = new Array(created.length);
  let nextIndex = 0;
  await Promise.all(
    Array.from({ length: Math.min(PHOTO_RESOLVE_CONCURRENCY, created.length) }, async () => {
      for (;;) {
        const i = nextIndex++;
        if (i >= created.length) return;
        resolvedOutcomes[i] = await resolvePhoto(created[i], deps);
      }
    }),
  );

  return { scanIds, outcomes: [...resolvedOutcomes, ...failedOutcomes] };
}
